import { FC, useMemo } from "react";
import { Chart as ChartJS, ChartData, ChartOptions, ScriptableContext, TooltipItem, registerables } from "chart.js";
import { Chart } from "react-chartjs-2";
import * as ChartGeo from "chartjs-chart-geo";
import { IChoroplethDataPoint } from "chartjs-chart-geo";
import { chartJsTextColor } from "@portal/helpers/chartJs";
import { COLORS, IRegion } from "./helper";
import GeoData from "./countries-50m.json";

// Register standard Chart.js components + Geo plugin elements
ChartJS.register(
  ...registerables,
  ChartGeo.ChoroplethController,
  ChartGeo.GeoFeature,
  ChartGeo.ColorScale,
  ChartGeo.ProjectionScale,
);

export interface IRegionChartProps {
  regions: IRegion[];
}

interface CountryGeometry {
  properties?: { name?: string };
}

interface RegionTopology {
  objects: {
    countries: { geometries: CountryGeometry[] };
    land: unknown;
  };
}

type RegionPoint = IChoroplethDataPoint & {
  color: string;
  feature: IChoroplethDataPoint["feature"] & { properties?: { name?: string } };
};

const topoData = GeoData as unknown as RegionTopology & Parameters<typeof ChartGeo.topojson.feature>[0];
const world = ChartGeo.topojson.feature(
  topoData,
  topoData.objects.land as Parameters<typeof ChartGeo.topojson.feature>[1],
) as IChoroplethDataPoint["feature"];
const normalizeName = (name: string) => name.trim().toLowerCase();

const RegionChart: FC<IRegionChartProps> = ({ regions }) => {
  const regionPolygons = useMemo<RegionPoint[]>(() => {
    if (!regions?.length) return [];

    const countryObject = topoData.objects.countries;
    const geometryByName = new Map(
      countryObject.geometries
        .filter(geometry => geometry.properties?.name)
        .map(geometry => [normalizeName(geometry.properties?.name ?? ""), geometry]),
    );

    return regions.flatMap(region => {
      const geometries = region.countryCodes
        .map(([, countryName]) => geometryByName.get(normalizeName(countryName)))
        .filter((geometry): geometry is CountryGeometry => Boolean(geometry));

      if (!geometries.length) return [];

      const mergedGeometry = ChartGeo.topojson.merge(
        topoData,
        geometries as unknown as Parameters<typeof ChartGeo.topojson.merge>[1],
      );

      return [
        {
          feature: {
            type: "Feature",
            properties: { name: region.name },
            geometry: mergedGeometry,
          },
          value: region.value ?? 0,
          color: region.color ?? COLORS[0],
        },
      ];
    });
  }, [regions]);

  const data: ChartData<"choropleth", RegionPoint[], string> = {
    labels: regionPolygons.map(r => r.feature.properties?.name ?? ""),
    datasets: [
      {
        label: "Regioner",
        outline: world as unknown as RegionPoint[],
        borderColor: chartJsTextColor(),
        outlineBorderColor: chartJsTextColor(),
        borderWidth: 1,
        outlineBorderWidth: 1,
        backgroundColor: (ctx: ScriptableContext<"choropleth">) => {
          const raw = ctx.raw as RegionPoint | undefined;
          return raw?.color ?? "#dbeafe";
        },
        data: regionPolygons,
      },
    ],
  };

  const options: ChartOptions<"choropleth"> = {
    responsive: true,
    maintainAspectRatio: false,
    showOutline: true,
    showGraticule: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (item: TooltipItem<"choropleth">) => {
            const raw = item.raw as RegionPoint | undefined;
            return `${raw?.feature?.properties?.name ?? ""}: ${(raw?.value ?? 0).toFixed(2)}%`;
          },
        },
      },
    },
    scales: {
      color: {
        axis: "x",
        display: false,
      },
      projection: {
        axis: "x",
        projection: "equalEarth",
      },
    },
  };

  return (
    <div className="w-full" style={{ height: 250 }}>
      <Chart type="choropleth" data={data} options={options} />
    </div>
  );
};

export default RegionChart;
